import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  MOBILE_SCREEN,
  useDeviceType,
  useHasMounted,
  useOnResizeDebounce,
} from '@travel/traveler-core/hooks';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { toSEOSearchString } from '@travel/traveler-core/utils/seo';
import { lazyWithSuspense } from '@travel/utils';

import CouponAccordion from 'components/CouponAccordion';

import { replaceLocation } from 'store/__router/actions';
import { getLimitationsForCurrentMarket } from 'store/markets/selectors';
import { getItem as getProviderItem } from 'store/providerInformation/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';
import { isClient } from 'store/utils';

import { OFFER_TYPE } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import type { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';

import type { URLParams } from '../../resolver';
import type { FiltersProps } from '../Filters';
import Filters from '../Filters';
import type { FilterFormProps } from '../HiddenFilterForm';
import ListingTypeSelectorTabList from '../ListingTypeSelectorTabList';
import RoomList from '../RoomList';

import styles from './roomAndPlanList.module.scss';

const PlanList = lazyWithSuspense(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-PlanList" */
      '../PlanList'
    ),
);

type Props = {
  query: ProviderInfoQuery;
  params: URLParams;
  providerInfoCanonicalUrl: string;
  shouldLazyLoad?: boolean;
  setIsOpenDatePicker: React.Dispatch<React.SetStateAction<boolean>>;
  activeListType: ProviderInfoListTypes;
  setActiveListType: React.Dispatch<React.SetStateAction<ProviderInfoListTypes>>;
  filterValProps: FilterFormProps;
  filtersProps: FiltersProps;
};

const TAB_EXISTENCE_REFRESH_INTERVAL = 100; // Refresh Interval time to update the width till the element is fully loaded

function RoomAndPlanList(props: Props) {
  const {
    query,
    params,
    providerInfoCanonicalUrl,
    shouldLazyLoad = false,
    setIsOpenDatePicker,
    activeListType,
    setActiveListType,
    filterValProps,
    filtersProps,
  } = props;

  const hasMounted = useHasMounted();
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isSP = deviceType === MOBILE_SCREEN;

  const [isPlanListVisited, setIsPlanListVisited] = useState<boolean>(
    query.listType === PROVIDER_LIST_TYPE.PLAN_LIST,
  );

  const providerItem = useSelector(getProviderItem);
  const currentCoupon = useSelector(getCouponDetails(query.salesPromotionId || ''));
  // tab scroll logics
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const prevActiveListTypeRef = useRef(activeListType);
  const tagFilterAndTabListWrapperRef = useRef<HTMLDivElement | null>(null);
  const roomPlanTabListRef = useRef<HTMLDivElement>(null);
  const [tabListWidth, setTabListWidth] = useState<number>(0);

  useEffect(() => {
    if (
      anchorRef.current &&
      prevActiveListTypeRef.current !== activeListType &&
      tagFilterAndTabListWrapperRef.current
    ) {
      const offsetTop = anchorRef.current?.offsetTop;
      const tagFilterAndTabListHeight = tagFilterAndTabListWrapperRef.current?.offsetHeight;
      window.scrollTo(0, offsetTop - tagFilterAndTabListHeight);
    }
  }, [activeListType]);

  const onMoreDateClick = useCallback(() => {
    setIsOpenDatePicker(true);
  }, [setIsOpenDatePicker]);

  const updateTabListWidth = () => {
    if (roomPlanTabListRef.current && window) {
      const width = roomPlanTabListRef.current?.getBoundingClientRect().width || 0;
      const computedStyle = window.getComputedStyle(roomPlanTabListRef.current);
      const marginLeft = computedStyle ? parseInt(computedStyle.marginLeft, 10) || 0 : 0;
      const marginRight = computedStyle ? parseInt(computedStyle.marginRight, 10) || 0 : 0;
      setTabListWidth(width + marginLeft + marginRight);
    }
  };

  useEffect(() => {
    if (!roomPlanTabListRef.current) {
      const tabListInterval = setInterval(() => {
        if (roomPlanTabListRef.current) {
          clearInterval(tabListInterval);
          updateTabListWidth();
        }
      }, TAB_EXISTENCE_REFRESH_INTERVAL);
      return () => clearInterval(tabListInterval);
    }

    updateTabListWidth();
  }, []);

  useOnResizeDebounce(updateTabListWidth);

  const limitations = useSelector(getLimitationsForCurrentMarket);
  const onRemoveCoupon = useCallback(() => {
    dispatch(
      replaceLocation({
        pathname: paths.providerInfo.pathResolver(providerItem.pathId),
        search: toSEOSearchString(
          {
            ...query,
            salesPromotionId: undefined,
            isCouponSpecified: undefined,
          },
          limitations,
        ),
      }),
    );
  }, [dispatch, limitations, providerItem.pathId, query]);

  const handleChangeListType = useCallback((listType: string) => {
    const url = new URL(window.location.toString());

    // reset auto-scroll of previous rate plan
    url.hash = '';

    if (url.searchParams.get('listType') !== listType && isClient) {
      listType === PROVIDER_LIST_TYPE.PLAN_LIST && setIsPlanListVisited(true);
      url.searchParams.set('listType', listType);
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const listingTypeSelectorTabList = (
    <ListingTypeSelectorTabList
      innerRef={roomPlanTabListRef}
      activeListType={activeListType}
      setActiveListType={setActiveListType}
      prevActiveListTypeRef={prevActiveListTypeRef}
      setIsAutoOpenDatePicker={setIsOpenDatePicker}
      onChangeListType={handleChangeListType}
    />
  );

  const roomsAndPlanListFilters = (
    <Filters
      listType={activeListType}
      pageIdentifier={params.pageIdentifier}
      query={query}
      listingTypeSelectorTabList={listingTypeSelectorTabList}
      tagFilterAndTabListRef={tagFilterAndTabListWrapperRef}
      tabListWidth={tabListWidth}
      onRemoveCoupon={onRemoveCoupon}
      filterValProps={filterValProps}
      filtersProps={filtersProps}
    />
  );

  const listClassName = {
    className: styles.contentBox,
    emptyClassName: styles.emptyContentBox,
  };
  const hiddenClassName = {
    className: styles.hiddenBox,
    emptyClassName: styles.hiddenBox,
  };

  return (
    <div data-testid="roomAndPlanList-wrapper">
      {isSP ? (
        <>{roomsAndPlanListFilters}</>
      ) : (
        <>
          <div ref={anchorRef} />
          {!isSP && (
            <h2 data-testid="providerInfo-searchFrom-header" className={styles.title}>
              <Translate id="Provider_Info.Explore_Our_Rooms_And_Plans" />
            </h2>
          )}
          <div className={styles.sticky}>
            <div className={styles.innerSticky}>{roomsAndPlanListFilters}</div>
          </div>
        </>
      )}
      {/* sales promotion */}
      {query.salesPromotionId && (
        <>
          {currentCoupon?.offerType !== OFFER_TYPE.DIRECT && (
            <CouponAccordion
              couponId={query.salesPromotionId}
              couponContentClassName={styles.couponContent}
              className={styles.couponWrapper}
              onRemoveCoupon={onRemoveCoupon}
            />
          )}
        </>
      )}

      <RoomList
        pageIdentifier={params.pageIdentifier}
        providerInfoCanonicalUrl={providerInfoCanonicalUrl}
        query={query}
        {...(activeListType === PROVIDER_LIST_TYPE.ROOM_LIST ? listClassName : hiddenClassName)}
        moreDateClick={onMoreDateClick}
      />
      {hasMounted && (!shouldLazyLoad || isPlanListVisited) && (
        <PlanList
          pageIdentifier={params.pageIdentifier}
          providerInfoCanonicalUrl={providerInfoCanonicalUrl}
          query={query}
          {...(activeListType === PROVIDER_LIST_TYPE.PLAN_LIST ? listClassName : hiddenClassName)}
          moreDateClick={onMoreDateClick}
        />
      )}
    </div>
  );
}

export default React.memo(RoomAndPlanList);
